/**
 * there's a lot of 'any' in this file, and that's because its a helper function that simplifies typing above it
 * but to do that, it has to have a few 'any' inside of it - we allow 'any' input and then infer types that match for the output
 */
type Selector<R, S> = (s: R) => S
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- we're using Parameters to infer the parameter types
type SelectorFactory<S, T> = (...args: any[]) => Selector<S, T>
type SelectorCollection<R> = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- we're useing ReturnType to infer the return type
    [name: string]: SelectorFactory<R, any>
}
type RemappedSelectorCollection<R, S, C extends SelectorCollection<S>> = {
    [P in keyof C]: (...args: Parameters<C[P]>) => Selector<R, ReturnType<ReturnType<C[P]>>>
}

/**
 * withRootSelector is a higher order function that takes a root function, a selector factory,
 * and returns a selector factory that maps the root down to a substate.
 * @param root the root function
 * @param selectorFactory the factory for a selector
 * @returns the selector, scoped down to only pass in a specific factory
 */
export function withRootSelector<
    R, // root state type
    S, // selected substate type
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- we're using ReturnType to infer the return type
    SF extends SelectorFactory<S, any>, // selector factory type
>(
    // the root function; takes the state, finds a substate
    root: (root: R) => S,
    // the selector function; takes the substate, pulls out a value
    selectorFactory: SF
): (...args: Parameters<SF>) => Selector<R, ReturnType<ReturnType<SF>>> {
    return (...args) => {
        const selector = selectorFactory(...args)
        return (r: R) => {
            const s = root(r)
            return selector(s)
        }
    }
}

/**
 * withRoot wraps up the selectors and remaps them under a common state
 * @param root selector from the root state to the substate
 * @param collection collection of selectors
 * @returns a collection of remapped selectors using the root state
 */
export function withRoot<
    R, // root state type
    S, // selected substate type
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- we're using ReturnType to infer the return type
    C extends { [name: string]: SelectorFactory<S, any> },
>(
    // the root function; takes the state, finds a substate
    root: (root: R) => S,
    // the selector collection
    collection: C
): RemappedSelectorCollection<R, S, C> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- we plan to iterate over the keys, but we need to start from a blank state
    const result: RemappedSelectorCollection<R, S, C> = {} as any
    for (const k in collection) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- the type inference here breaks because we
        result[k] = withRootSelector(root, collection[k]) as any
    }
    return result
}
